/** @jsx jsx */
import React, { type FC, type ReactNode } from 'react';
import { jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { Box, Text, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import { fg } from '@atlassian/jira-feature-gating';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants/src/ecosystem-constants.tsx';
import type { ActivityItem } from '@atlassian/jira-issue-view-common-types/src/activity-item-type.tsx';
import { COMMENTS } from '@atlassian/jira-issue-view-common-constants/src/activity-items.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	ForgeIssueActivityItem,
	ForgeIssueActivityLabel,
} from '@atlassian/jira-issue-view-forge-issue-activity/src/index.tsx';
import {
	ContextualAnalyticsData,
	fireScreenAnalyticsDeferred,
	MountEvent,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import ToggleButtons from '@atlassian/toggle-buttons';
import { fireUiTriggerClickedEvent } from '@atlassian/jira-forge-issue-activity/src/analytics/services/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { SelectActivityItem } from '../../common/ui/types.tsx';
import { buttonTheme } from '../../styled.tsx';
import { messages } from '../../messages.tsx';
import { ufoNameForSwitchingToActivityItem } from './utils.tsx';

const ButtonComponentNew = ({
	children,
	onClick,
	isSelected,
	testId,
	item,
}: {
	children: ReactNode;
	onClick: (event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	isSelected: boolean;
	testId?: string;
	item: ActivityItem;
}) => {
	const interactionName = ufoNameForSwitchingToActivityItem(item);
	return (
		<CustomThemeButton
			appearance="subtle"
			spacing="compact"
			isSelected={isSelected}
			theme={buttonTheme(isSelected)}
			onClick={onClick}
			testId={testId}
			aria-checked={isSelected}
			role="menuitemradio"
			// eslint-disable-next-line jira/ufo/valid-labels
			interactionName={interactionName}
		>
			{children}
		</CustomThemeButton>
	);
};

const ButtonComponentOld = ({
	children,
	onClick,
	isSelected,
	testId,
}: {
	children: ReactNode;
	onClick: (event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	isSelected: boolean;
	testId?: string;
}) => (
	<CustomThemeButton
		appearance="subtle"
		spacing="compact"
		isSelected={isSelected}
		theme={buttonTheme(isSelected)}
		onClick={onClick}
		testId={testId}
		aria-checked={isSelected}
		role="menuitemradio"
		interactionName="auto-generated.jira-issue-activity-feed.ui.buttons.button"
	>
		{children}
	</CustomThemeButton>
);

type Props = {
	items: ActivityItem[];
	selectedItem: ActivityItem;
	onSelectActivityItem: SelectActivityItem;
	unreadCommentsCount?: number;
};

const MAX_UNREAD_COUNT = 4;
const renderNewActivityFeedButtons = (
	{ items, selectedItem, onSelectActivityItem, unreadCommentsCount }: Props,
	formatMessage: FormatMessage,
) => {
	const options = items.map((item: ActivityItem) => {
		let label: ReactNode = item.name;
		if (item.type === FORGE_ENTITY_TYPE) {
			label = <ForgeIssueActivityLabel item={item} />;
		} else if (item.type === COMMENTS) {
			if (
				unreadCommentsCount &&
				expVal('thor_issue_view_realtime_updates_experiment', 'isEnabled', false)
			) {
				const unreadCountText =
					unreadCommentsCount > MAX_UNREAD_COUNT
						? `${MAX_UNREAD_COUNT}+`
						: `+${unreadCommentsCount}`;
				label = (
					<>
						<Text>{item.name}</Text>
						<Box as="span" xcss={unreadStyles}>
							<Text>{unreadCountText}</Text>
						</Box>
					</>
				);
			} else {
				label = item.name;
			}
		}

		return {
			id: item.key,
			label,
			interactionName: ufoNameForSwitchingToActivityItem(item),
			testId: `issue-activity-feed.ui.buttons.${item.key}`,
		};
	});

	return (
		<ToggleButtons
			options={options}
			selectedOption={selectedItem.key}
			onChange={(id, event, analyticsEvent) => {
				const selectedOption = items.find((item: ActivityItem) => item.key === id);
				if (!selectedOption) {
					return;
				}

				if (selectedOption?.type === FORGE_ENTITY_TYPE) {
					fireUiTriggerClickedEvent(selectedOption.extension.id);
				}

				if (analyticsEvent) {
					onSelectActivityItem(
						selectedOption,
						analyticsEvent,
						'buttonItemSelected',
						event.timeStamp,
					);
				}
			}}
			label={formatMessage(messages.filterByLabel)}
		/>
	);
};

const renderOldActivityFeedButtons = ({
	items,
	selectedItem,
	onSelectActivityItem,
	unreadCommentsCount,
}: Props) =>
	items.map((item: ActivityItem) => {
		const props = {
			key: item.key,
			isSelected: selectedItem.key === item.key,

			onClick: (event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) =>
				onSelectActivityItem(item, analyticsEvent, 'buttonItemSelected', event.timeStamp),
		};

		/**
		 * Todo: Update ForgeIssueActivityItem to use ButtonComponentNew
		 *
		 * After the cleanup of ken-1285-fix-ufo-event-for-issue-history,
		 * ForgeIssueActivityItem will keep using the ButtonComponentOld.
		 * This is done to avoid any changes to ForgeIssueActivityItem for now
		 *
		 * Updating ForgeIssueActivityItem to use ButtonComponentNew will be done in a separate task
		 */
		if (item.type === FORGE_ENTITY_TYPE) {
			return (
				<ForgeIssueActivityItem
					{...props}
					key={item.key}
					component={ButtonComponentOld}
					item={item}
				/>
			);
		}

		let unreadCommentBadge;
		if (
			item.type === COMMENTS &&
			unreadCommentsCount &&
			expVal('thor_issue_view_realtime_updates_experiment', 'isEnabled', false)
		) {
			unreadCommentBadge = (
				<Box as="span" xcss={badgeStyles}>
					<Badge max={MAX_UNREAD_COUNT} appearance="primary">
						{unreadCommentsCount}
					</Badge>
				</Box>
			);
		}

		if (fg('ken-1285-fix-ufo-event-for-issue-history')) {
			return (
				<ButtonComponentNew
					{...props}
					key={item.key}
					testId={`issue-activity-feed.ui.buttons.${item.key}`}
					item={item}
				>
					{item.name}
					{unreadCommentBadge}
				</ButtonComponentNew>
			);
		}
		return (
			<ButtonComponentOld
				{...props}
				key={item.key}
				testId={`issue-activity-feed.ui.buttons.${item.key}`}
			>
				{item.name}
				{unreadCommentBadge}
			</ButtonComponentOld>
		);
	});

export const ActivityFeedButtons: FC<Props> = (props: Props) => {
	const { formatMessage } = useIntl();

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="activityFeedButtons">
			<MountEvent onMount={(event) => fireScreenAnalyticsDeferred(event)} />
			{isVisualRefreshEnabled() && fg('visual-refresh_drop_5')
				? renderNewActivityFeedButtons(props, formatMessage)
				: renderOldActivityFeedButtons(props)}
		</ContextualAnalyticsData>
	);
};

const unreadStyles = xcss({
	backgroundColor: 'color.background.accent.blue.subtle.hovered',
	borderRadius: 'border.radius.050',
	color: 'color.text',
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	marginLeft: 'space.050',
});

const badgeStyles = xcss({
	marginLeft: 'space.050',
});
